import React, { useCallback, useEffect, useMemo } from "react";

import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import {
  CategoryScale,
  ChartDataset,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { ValueLabel } from "../sparkline/SparkLineHandler";
import { AdvancedProps, LineConfig } from "../sparkline/SparkLineTypes";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler);

type SparkLineChartProps = {
  dataArray: number[];
  advancedProps: AdvancedProps;
  lineColor?: string;
  lineWidth?: number;
  setValueLabel: React.Dispatch<React.SetStateAction<ValueLabel>>;
};

const options = (showYAxis = false): ChartOptions<"line"> => ({
  animation: {
    duration: 0,
  },
  plugins: {
    tooltip: {
      enabled: false,
    },
    legend: {
      display: false,
    },
  },
  interaction: {
    intersect: false,
  },
  scales: {
    x: {
      display: false,
    },
    y: {
      display: showYAxis && 'auto',
    },
  },
});

const SparkLineChart: React.FC<SparkLineChartProps> = ({
  dataArray,
  advancedProps,
  lineColor,
  lineWidth,
  setValueLabel,
}) => {
  const queryOptions = (enabled = false) => ({
    count: advancedProps.count,
    refTime: advancedProps.referenceTime,
    options: {
      refetchInterval: advancedProps.updateFrequency ? advancedProps.updateFrequency * 1000 : 0,
      enabled,
    },
  });

  const { data: baselineDatasetData } = useGetRecentData(
    [advancedProps.baseline?.dataset!],
    queryOptions(!!advancedProps.baseline?.dataset)
  );
  const { data: minLineDatasetData } = useGetRecentData(
    [advancedProps.minLine?.dataset!],
    queryOptions(!!advancedProps.minLine?.dataset)
  );
  const { data: maxLineDatasetData } = useGetRecentData(
    [advancedProps.maxLine?.dataset!],
    queryOptions(!!advancedProps.maxLine?.dataset)
  );

  const getValues = useCallback((config?: LineConfig, data?: InstantDoubleValue[]) => {
    if (config?.usage === "constant") return Array(advancedProps.count).fill(config.constant);
    if (config?.usage === "dataset" && data) return data.map(item => item.value);
    return [];
  }, [advancedProps.count]);

  const baselineValues = getValues(advancedProps.baseline, baselineDatasetData?.[0]?.data);
  const minValues = useMemo(
    () => getValues(advancedProps.minLine, minLineDatasetData?.[0]?.data),
    [advancedProps.minLine, getValues, minLineDatasetData]
  );
  const maxValues = useMemo(
    () => getValues(advancedProps.maxLine, maxLineDatasetData?.[0]?.data),
    [advancedProps.maxLine, getValues, maxLineDatasetData]
  );

  useEffect(() => {
    if (dataArray.at(-1)! >= maxValues.at(-1)) {
      setValueLabel(prev => ({ ...prev, textColor: advancedProps.maxLine?.color }));
    } else if (dataArray.at(-1)! <= minValues.at(-1)) {
      setValueLabel(prev => ({ ...prev, textColor: advancedProps.minLine?.color }));
    } else {
      setValueLabel(prev => ({ ...prev, textColor: "inherit" }));
    }
  }, [
    advancedProps.maxLine?.color,
    advancedProps.minLine?.color,
    dataArray,
    maxValues,
    minValues,
    setValueLabel,
  ]);

  const baselineDataChart: ChartDataset<"line", number[]> = {
    data: baselineValues,
    borderColor: advancedProps.baseline?.color,
    borderWidth: advancedProps.baseline?.width,
    pointStyle: false,
  };
  const minValueLine: ChartDataset<"line", number[]> = {
    data: minValues,
    borderColor: advancedProps.minLine?.color,
    borderWidth: 1,
    borderDash: [2, 5],
    pointStyle: false,
  };
  const maxValueLine: ChartDataset<"line", number[]> = {
    data: maxValues,
    borderColor: advancedProps.maxLine?.color,
    borderWidth: 1,
    borderDash: [3, 5],
    pointStyle: false,
 
  };

  const lineOptions = options(advancedProps.showYAxis);

  return (
    <Line
      data={{
        labels: dataArray.map(() => 1),
        datasets: [
          {
            data: dataArray,
            borderColor: "transparent",
            borderWidth: 0,
            pointStyle: false,

            fill: {
              target: 4,
              below: advancedProps.minLine?.color,
              above: "transparent",
            },
          },
          {
            data: dataArray,
            borderColor: "transparent",
            borderWidth: 0,
            pointStyle: false,

            fill: {
              target: 5,
              below: "transparent",
              above: advancedProps.maxLine?.color,
            },
          },
          {
            data: dataArray,
            borderColor: lineColor,
            borderWidth: lineWidth,
            pointStyle: false,

            fill: {
              target: 3,
              above: advancedProps.baseline?.fillAboveColor,
              below: advancedProps.baseline?.fillBelowColor,
            },
          },
          baselineDataChart,
          minValueLine,
          maxValueLine,
        ],
      }}
      options={lineOptions}
      style={{ flex: 2 }}
    />
  );
};

export default SparkLineChart;
